/**
 * Custom hooks for OKR and Key Result comments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { commentService } from '../services/comment.service';
import { toast } from 'react-hot-toast';
import type { CreateCommentRequest } from '../types/comment';

// Type for API error responses
interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

/**
 * Hook to fetch comments for an OKR
 */
export const useComments = (okrId: string) => {
  return useQuery({
    queryKey: ['comments', 'okr', okrId],
    queryFn: () => commentService.getComments(okrId),
    enabled: !!okrId,
  });
};

/**
 * Hook to fetch comments for a Key Result
 */
export const useKeyResultComments = (keyResultId: string) => {
  return useQuery({
    queryKey: ['comments', 'keyResult', keyResultId],
    queryFn: () => commentService.getKeyResultComments(keyResultId),
    enabled: !!keyResultId,
  });
};

/**
 * Hook to create a new comment (for OKR or Key Result)
 */
export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCommentRequest) => commentService.createComment(data),
    onSuccess: (_, variables) => {
      // Invalidate the appropriate comments query based on target
      if (variables.keyResultId) {
        queryClient.invalidateQueries({ queryKey: ['comments', 'keyResult', variables.keyResultId] });
      } else if (variables.okrId) {
        queryClient.invalidateQueries({ queryKey: ['comments', 'okr', variables.okrId] });
      }
      toast.success('Comment added');
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.error || 'Failed to add comment';
      toast.error(message);
    }
  });
};
